// HU-40 · Constancias de aprobación del FAR-03 (spec `ui/MG-ILE-65.md`, tarea 40-6).
//
// SIN PANTALLA PROPIA: es la tabla de solo lectura que la Aprobación del informe muestra en la
// pestaña «Constancias» (contenedor `aprobacion-constancias`) y que el Expediente mostrará en
// la fila del FAR-03 (`expediente-constancias`, MG-ILE-54). El contenedor lo pone quien la usa;
// la fila y las celdas llevan el mismo testid en todas partes.
//
// TODO LO QUE SE VE VIENE DEL SERVIDOR (§8.1 R1). La constancia la escribe el backend en la
// MISMA transacción del acto (RN-48, HU-40-C1): quién y cargo salen del token, el equipo de
// origen del proxy de confianza (C8). No hay acción sobre ella: no se edita ni se borra (C4).
// Sin imagen de firma (C7, ADR-009). La acción y el documento llegan como código y se leen
// con el glosario (`lib/etiquetas.ts`); la fecha, con `lib/fechas.ts`.

import { ShieldCheck } from 'lucide-react';
import { DataTable, EmptyState, Mono, StatusBadge } from './ui';
import type { ColumnaDeTabla, FilaDeTabla } from './ui';
import type { ConstanciaDelInforme } from '../api/evaluacion';
import { etiquetaDeLaAccionDeConstancia, etiquetaDelDocumentoDeConstancia } from '../lib/etiquetas';
import { comoFechaYHora } from '../lib/fechas';

const COLUMNAS: ColumnaDeTabla[] = [
  { key: 'accion', label: 'Acción', testid: 'constancia-accion' },
  { key: 'documento', label: 'Documento', testid: 'constancia-documento' },
  { key: 'version', label: 'Versión', testid: 'constancia-version' },
  { key: 'quien', label: 'Quién', testid: 'constancia-quien' },
  { key: 'momento', label: 'Fecha y hora', testid: 'constancia-momento' },
  { key: 'equipo', label: 'Equipo de origen', testid: 'constancia-equipo' },
  { key: 'motivo', label: 'Motivo', testid: 'constancia-motivo' },
];

/** Una celda: el dato del contrato tal cual, leído con el glosario o el formateador de fechas. */
function Celda({ c, columna, zonaHoraria }: Readonly<{ c: ConstanciaDelInforme; columna: string; zonaHoraria: string | null }>) {
  switch (columna) {
    case 'accion': return <>{etiquetaDeLaAccionDeConstancia(c.accion)}</>;
    case 'documento': return <>{etiquetaDelDocumentoDeConstancia(c.documento_tipo)}</>;
    case 'version': return <StatusBadge tone="neutral" dot={false}>v{c.version}</StatusBadge>;
    case 'quien': return (
      <span>
        <span className="font-medium">{c.firmante_nombre}</span>
        {c.firmante_cargo && <span className="block text-meta text-ink-soft">{c.firmante_cargo}</span>}
      </span>
    );
    case 'momento': return <Mono className="text-meta">{comoFechaYHora(c.ocurrido_en, zonaHoraria)}</Mono>;
    case 'equipo': return <Mono className="text-meta">{c.direccion_ip ?? ''}</Mono>;
    case 'motivo': return <>{c.motivo ?? ''}</>;
    default: return null;
  }
}

export default function ConstanciasDelInforme({ constancias, zonaHoraria }: Readonly<{
  constancias: ConstanciaDelInforme[];
  /** RN-82: la zona del país del caso (`useZonaDelPais`), en la que se lee la hora; `null` = Lima. */
  zonaHoraria: string | null;
}>) {
  if (constancias.length === 0) {
    return <EmptyState data-testid="constancia-vacio" icon={ShieldCheck} title="Todavía sin constancias" />;
  }
  // `id` en la fila: la clave de React es la constancia, no su posición.
  const filas: FilaDeTabla[] = constancias.map((c) => ({ id: c.constancia_id, constancia: c }));
  return (
    <div data-testid="constancia-exito">
      <DataTable sinMarco columns={COLUMNAS} rows={filas} testidDeFila="constancia-fila"
        renderCell={(fila, columna) => <Celda c={fila.constancia as ConstanciaDelInforme} columna={columna.key} zonaHoraria={zonaHoraria} />} />
    </div>
  );
}
